import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
         DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, PackagePlus, Truck, Warehouse } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "@/lib/AuthContext";

const n = (v) => Number(v || 0).toLocaleString();
const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || fallback;
};

/** Surplus Sweep — every leftover unit, and the job that wants it. */
export default function SurplusSweep({ projectId = "", onChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [claim, setClaim] = useState(null);   // { row, qty, to, mode, note }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData((await api.get("/material/surplus",
        { params: projectId ? { project_id: projectId } : {} })).data);
    } catch { toast.error("Could not read the surplus list"); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => data?.rows || [], [data]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/material/surplus/claim", {
        placement_id: claim.row.placement_id, to_project_id: claim.to,
        qty: Number(claim.qty), mode: claim.mode,
        by: user?.email || user?.name || "", note: claim.note || "",
      });
      toast.success(res.message);
      setClaim(null);
      await load();
      onChanged?.();      // a claim creates a return — whoever is showing that queue reloads
    } catch (e) { toast.error(errText(e, "That claim could not be made")); }
    finally { setBusy(false); }
  };

  if (!data) {
    return <div className="p-6 text-center" data-testid="surplus-loading">
      <Loader2 className="w-4 h-4 animate-spin inline" />
    </div>;
  }

  return (
    <div className="space-y-2" data-testid="surplus-sweep">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-wide font-semibold text-gray-600 flex items-center gap-1">
          <PackagePlus className="w-3.5 h-3.5 text-amber-700" /> Surplus on site
        </span>
        <span className="text-[11px] text-gray-500">
          more was delivered than the FRS now asks for — claim it for another job before closeout
        </span>
        <span className="ml-auto text-[11px] tabular-nums font-semibold"
              data-testid="surplus-total">{n(data.units)} unit(s)</span>
      </div>

      <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-[11px]" data-testid="surplus-table">
          <thead className="text-[10px] uppercase text-gray-500 bg-gray-50">
            <tr>
              <th className="px-2 py-1 text-left">Job</th>
              <th className="px-2 py-1 text-left">Product</th>
              <th className="px-2 py-1 text-left">Where it is</th>
              <th className="px-2 py-1 text-left">Owning room</th>
              <th className="px-2 py-1 text-right">Spare</th>
              <th className="px-2 py-1 text-right">Days</th>
              <th className="px-2 py-1"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.placement_id} data-testid={`surplus-row-${r.placement_id}`}
                  className={`border-t border-gray-100 ${r.days_sitting >= 14 ? "bg-amber-50/70" : ""}`}>
                <td className="px-2 py-1 whitespace-nowrap">{r.job_number} {r.project_name}</td>
                <td className="px-2 py-1 font-medium">{r.product_short || "—"}</td>
                <td className="px-2 py-1 text-gray-700">{r.location_label}</td>
                <td className="px-2 py-1 text-gray-500">{r.owning_room}</td>
                <td className="px-2 py-1 text-right tabular-nums font-semibold">{n(r.claimable)}</td>
                <td className={`px-2 py-1 text-right tabular-nums ${r.days_sitting >= 14
                  ? "text-amber-800 font-semibold" : "text-gray-500"}`}>{r.days_sitting}</td>
                <td className="px-2 py-1 text-right">
                  <button data-testid={`surplus-claim-${r.placement_id}`}
                          onClick={() => setClaim({ row: r, qty: r.claimable, mode: "shop",
                                                    to: "", note: "" })}
                          className="px-1.5 py-0.5 rounded border border-blue-300 text-blue-700 hover:bg-blue-50">
                    Claim
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-2 py-6 text-center text-gray-500"
                      data-testid="surplus-empty">
                No surplus anywhere — every delivered unit is accounted for.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!claim} onOpenChange={(v) => { if (!v) setClaim(null); }}>
        <DialogContent className="max-w-md" data-testid="surplus-claim-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">
              Claim {claim?.row?.product_short} from{" "}
              {claim?.row?.job_number ? `job ${claim.row.job_number}` : claim?.row?.project_name}
            </DialogTitle>
            <DialogDescription className="text-[11px]">
              {n(claim?.row?.claimable)} spare at <b>{claim?.row?.location_label}</b>. It leaves
              that job the moment you claim it.
            </DialogDescription>
          </DialogHeader>

          <label className="text-[10px] uppercase tracking-wide text-gray-600">
            Units
            <Input type="number" min="1" max={claim?.row?.claimable} value={claim?.qty ?? ""}
                   data-testid="surplus-claim-qty" className="h-8"
                   onChange={(e) => setClaim((c) => ({ ...c, qty: e.target.value }))} />
          </label>

          <label className="text-[10px] uppercase tracking-wide text-gray-600">
            Which job takes it
            <select value={claim?.to || ""} data-testid="surplus-claim-project"
                    onChange={(e) => setClaim((c) => ({ ...c, to: e.target.value }))}
                    className="block w-full h-8 text-xs border border-gray-300 rounded px-2 bg-white">
              <option value="">Choose a job…</option>
              {(data.destinations || []).filter((d) => d.project_id !== claim?.row?.project_id)
                .map((d) => (
                <option key={d.project_id} value={d.project_id}>
                  {d.job_number ? `${d.job_number} — ` : ""}{d.project_name}
                </option>
              ))}
            </select>
          </label>

          <div className="text-[10px] uppercase tracking-wide text-gray-600">How does it travel</div>
          <div className="flex gap-1">
            {[["shop", "Back through the shop", Warehouse,
               "The warehouse confirms receipt, then it lands on the claiming job"],
              ["direct", "Site to site", Truck,
               "It lands on the claiming job now and fires their follow-ups"]].map(
              ([k, label, Icon, hint]) => (
              <button key={k} onClick={() => setClaim((c) => ({ ...c, mode: k }))}
                      data-testid={`surplus-claim-mode-${k}`} data-active={claim?.mode === k}
                      title={hint}
                      className={`flex-1 text-left px-2 py-1.5 rounded border text-[11px] ${
                        claim?.mode === k ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-gray-700 border-gray-300"}`}>
                <Icon className="w-3 h-3 inline mr-1" />{label}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-gray-500">
            {claim?.mode === "direct"
              ? "Site to site: on hand for the claiming job straight away — no shop stop."
              : "Through the shop: in transit until the warehouse confirms receipt, then it is on hand for the claiming job."}
          </p>

          <label className="text-[10px] uppercase tracking-wide text-gray-600">
            Note (optional)
            <Input value={claim?.note || ""} placeholder="who is driving it, when"
                   data-testid="surplus-claim-note" className="h-8"
                   onChange={(e) => setClaim((c) => ({ ...c, note: e.target.value }))} />
          </label>

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setClaim(null)}
                    data-testid="surplus-claim-cancel">Cancel</Button>
            <Button size="sm" disabled={busy || !claim?.to || !Number(claim?.qty)}
                    onClick={submit} data-testid="surplus-claim-save">
              {busy ? "Moving…" : "Claim it"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
